import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const TYPE_LABELS = {
  asset: 'Varlık',
  contra_asset: 'Varlık (negatif)',
  liability: 'Pasif',
  revenue: 'Gelir',
  contra_revenue: 'Gelir (iade)',
  expense: 'Gider',
};

const emptyForm = {
  code: '', title: '', type: 'asset', parent_code: '',
  is_main: false, normal_balance: 'D', description: '', is_active: true,
};

function ChartOfAccountsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ search: '', type: '', scope: '' }); // scope: '' | 'global' | 'company'
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/chart-of-accounts');
      if (res.data.success) setItems(res.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => {
    const s = filter.search.toLowerCase().trim();
    return items.filter((x) => {
      if (filter.type && x.type !== filter.type) return false;
      if (filter.scope === 'global' && !x.is_global) return false;
      if (filter.scope === 'company' && x.is_global) return false;
      if (s && !(x.code.toLowerCase().includes(s) || x.title.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [items, filter]);

  const handleNew = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setMsg(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    if (row.is_system) { setMsg({ type: 'error', text: 'Sistem kayıtları düzenlenemez. Kendi alt hesabınızı ekleyin.' }); return; }
    if (row.is_global) { setMsg({ type: 'error', text: 'Global kayıtlar düzenlenemez (sadece şirket kayıtlarınız).' }); return; }
    setForm({ ...row, is_main: !!row.is_main, is_active: !!row.is_active });
    setEditId(row.id);
    setMsg(null);
    setShowModal(true);
  };

  const handleClose = () => { setShowModal(false); setMsg(null); };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      if (editId) {
        const res = await api.put(`/accounting/chart-of-accounts/${editId}`, form);
        if (!res.data.success) throw new Error(res.data.message);
      } else {
        const res = await api.post('/accounting/chart-of-accounts', form);
        if (!res.data.success) throw new Error(res.data.message);
      }
      await fetchList();
      setShowModal(false);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message });
    } finally { setSaving(false); }
  };

  const handleDelete = async (row) => {
    if (row.is_system || row.is_global) return;
    if (!window.confirm(`${row.code} ${row.title} silinsin mi?`)) return;
    try {
      const res = await api.delete(`/accounting/chart-of-accounts/${row.id}`);
      if (!res.data.success) throw new Error(res.data.message);
      await fetchList();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Hesap Planı (TDHP)</h2>
        <input
          type="text" placeholder="Ara: kod veya hesap adı..."
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          style={{ padding: '0.4rem 0.6rem', minWidth: 250, borderRadius: 4, border: '1px solid var(--border)' }}
        />
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          style={{ padding: '0.4rem 0.6rem', borderRadius: 4, border: '1px solid var(--border)' }}>
          <option value="">Tüm tipler</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filter.scope} onChange={(e) => setFilter({ ...filter, scope: e.target.value })}
          style={{ padding: '0.4rem 0.6rem', borderRadius: 4, border: '1px solid var(--border)' }}>
          <option value="">Tümü</option>
          <option value="global">Sadece global</option>
          <option value="company">Sadece şirket</option>
        </select>
        <button onClick={handleNew} style={{ padding: '0.4rem 1rem', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          + Yeni Hesap
        </button>
        <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
          {filtered.length} / {items.length} kayıt
        </span>
      </div>

      {loading ? <div>Yükleniyor...</div> : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead style={{ background: 'var(--bg-secondary)' }}>
              <tr>
                <th style={th}>Kod</th>
                <th style={th}>Hesap Adı</th>
                <th style={th}>Tip</th>
                <th style={th}>Üst Hesap</th>
                <th style={th}>Bakiye Yönü</th>
                <th style={th}>Kapsam</th>
                <th style={th}>Durum</th>
                <th style={th}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}><strong>{row.code}</strong></td>
                  <td style={td}>{row.title}</td>
                  <td style={td}>{TYPE_LABELS[row.type] || row.type}</td>
                  <td style={td}>{row.parent_code || '—'}</td>
                  <td style={td}>{row.normal_balance === 'D' ? 'Borç' : 'Alacak'}</td>
                  <td style={td}>
                    {row.is_global
                      ? <span style={{ ...badge, background: '#666' }}>Global {row.is_system ? '(sistem)' : ''}</span>
                      : <span style={{ ...badge, background: 'var(--accent, #2563eb)' }}>Şirket</span>}
                  </td>
                  <td style={td}>{row.is_active ? '✓' : '✗'}</td>
                  <td style={td}>
                    <button onClick={() => handleEdit(row)} disabled={row.is_global}
                      style={btnSm}>Düzenle</button>
                    {!row.is_global && (
                      <button onClick={() => handleDelete(row)} style={{ ...btnSm, marginLeft: 4, background: '#dc2626', color: '#fff' }}>
                        Sil
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)' }}>Kayıt yok</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={modalOverlay} onClick={handleClose}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editId ? 'Hesap Düzenle' : 'Yeni Hesap'}</h3>
            <div style={formGrid}>
              <label>Kod*</label>
              <input value={form.code} disabled={!!editId}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="örn. 102.01" style={input} />

              <label>Hesap Adı*</label>
              <input value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="örn. İŞ BANKASI" style={input} />

              <label>Tip*</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} style={input}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>

              <label>Üst Hesap</label>
              <input value={form.parent_code || ''}
                onChange={(e) => setForm({ ...form, parent_code: e.target.value })}
                placeholder="örn. 102 (boş bırakılabilir)" style={input} />

              <label>Bakiye Yönü*</label>
              <select value={form.normal_balance} onChange={(e) => setForm({ ...form, normal_balance: e.target.value })} style={input}>
                <option value="D">Borç (Debit)</option>
                <option value="C">Alacak (Credit)</option>
              </select>

              <label>Ana hesap mı?</label>
              <input type="checkbox" checked={!!form.is_main} onChange={(e) => setForm({ ...form, is_main: e.target.checked })} />

              <label>Aktif</label>
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />

              <label>Açıklama</label>
              <input value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })} style={input} />
            </div>

            {msg && <div style={{ ...alertStyle, ...(msg.type === 'error' ? alertError : alertSuccess) }}>{msg.text}</div>}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={handleClose} style={btn}>İptal</button>
              <button onClick={handleSave} disabled={saving} style={{ ...btn, background: 'var(--accent)', color: '#fff' }}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.6rem 0.8rem', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' };
const td = { padding: '0.5rem 0.8rem', fontSize: '0.9rem' };
const badge = { padding: '0.15rem 0.5rem', borderRadius: 4, color: '#fff', fontSize: '0.75rem' };
const btnSm = { padding: '0.25rem 0.6rem', fontSize: '0.8rem', border: '1px solid var(--border)', background: 'var(--bg-secondary)', borderRadius: 3, cursor: 'pointer' };
const btn = { padding: '0.4rem 1rem', border: '1px solid var(--border)', background: 'var(--bg-secondary)', borderRadius: 4, cursor: 'pointer' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal = { background: 'var(--bg-primary)', padding: '1.5rem', borderRadius: 8, minWidth: 480, maxWidth: 600, color: 'var(--text-primary)' };
const formGrid = { display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.6rem 1rem', alignItems: 'center' };
const input = { padding: '0.4rem 0.6rem', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.9rem', background: 'var(--bg-secondary)', color: 'var(--text-primary)' };
const alertStyle = { marginTop: '0.8rem', padding: '0.5rem 0.8rem', borderRadius: 4, fontSize: '0.85rem' };
const alertError = { background: '#fee', color: '#900', border: '1px solid #fcc' };
const alertSuccess = { background: '#efe', color: '#060', border: '1px solid #cfc' };

export default ChartOfAccountsPage;
